import React, { useState } from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import { DropdownButtonItem } from 'components/buttons/BzDropdownButton';
import Button from 'react-bootstrap/Button';
import { RequestMatcherItem } from 'components/transactionEditor/RequestMatchersList';
import styles from 'components/transactionEditor/ActionButtons.module.css';
import {
  transactionMatchersDropdownOperators,
  wizardMatchersDropdownOperators,
} from 'views/AssetCatalog/Transactions/TransactionEditor/TransactionEditor';
import { getKeyByValue } from 'utils/miscUtil';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import DataDrivenWarning from './DataDrivenWarning';
import { containsUnsupportedMatcherWithTemplateSyntax } from '../../utils/transactionUtils';
import { isPrivateCloud } from '../../utils/bzmUserConfigUtil';
import BzmIcon from '../icons/BzmIcon';
import MatcherOperatorDropdown from './MatcherOperatorDropdown';
import { Matcher } from '../../types/rest/dslTypes';
import _ from 'lodash';
import CheckBox from '../ui/checkBox/CheckBox';
import { createAiAssistantIcon } from '../../views/AssetCatalog/AiAssistant/aiAssistantUtil';
import AiAssistantModal from '../../views/AssetCatalog/AiAssistant/AiAssistantModal';
import { getAccountAiConsent } from '../../utils/bzmUserConfigUtil';
import { clearConversationId } from '../../store/aiConversations/aiConversationsActions';

interface RequestMatcherProps {
  id: string;
  matcherValues: RequestMatcherItem;
  onChange: (name: string, value: string | boolean, id: string) => void;
  onDelete: () => void;
  onAdd: (operator: string) => void;
  displayAdd: boolean;
  matcherTitle: string;
  isReadOnly?: boolean;
  isWizardModal?: boolean;
  shortTitle?: boolean;
  showIsRequiredCheckbox?: boolean;
  aiAssistantTitle?: string;
  transactionId?: number;
}

const RequestMatcher = (props: RequestMatcherProps) => {
  const [showAiAssistant, setShowAiAssistant] = useState(false);
  const [aiAssistantCallerId, setAiAssistantCallerId] = useState<string>('');
  const dispatch = useDispatch();

  const resolvedTxMatchersDropdownOperators =
    props.isWizardModal === true ? wizardMatchersDropdownOperators : transactionMatchersDropdownOperators;

  const { t } = useTranslation('transactions');

  const defaultOperator: DropdownButtonItem = {
    key: Matcher.equals,
    title: transactionMatchersDropdownOperators[Matcher.equals],
  };

  const operatorToSelect: DropdownButtonItem =
    props.matcherValues && props.matcherValues.operator
      ? {
          key: props.matcherValues.operator,
          title: resolvedTxMatchersDropdownOperators[props.matcherValues.operator] || props.matcherValues.operator,
        }
      : defaultOperator;

  const handleChange = (e: any, id: string) => {
    const target = e.target as HTMLInputElement;
    props.onChange(target.name, target.value, id);
  };

  const isAbsent = getKeyByValue(resolvedTxMatchersDropdownOperators, props.matcherValues.operator) === 'absent';
  const isDataWarningShown =
    !isPrivateCloud() &&
    containsUnsupportedMatcherWithTemplateSyntax(props.matcherValues.operator, props.matcherValues.value);

  if (props.isWizardModal === true && props.matcherValues.operator !== 'equals') {
    props.onChange('operator', 'equals', props.id);
  }

  const dropdownOperators = _.map(resolvedTxMatchersDropdownOperators, (item, key) => {
    return Matcher[key];
  });

  const handleAiAssistantShow = (callerId: string) => {
    setAiAssistantCallerId(callerId);
    setShowAiAssistant(true);
  };

  const handleAiAssistantUpdate = (value: string) => {
    props.onChange('value', value, props.id);
  };

  const currentValue = isAbsent ? '' : props.matcherValues.value ? props.matcherValues.value : '';

  const isExpressionOperator =
    props.matcherValues.operator === Matcher.matches || props.matcherValues.operator === Matcher.not_matches;
  const shouldShowAiIcon = isExpressionOperator && !props.isReadOnly && getAccountAiConsent();
  const stableNewTxSuffix = React.useRef(`new-${Date.now()}`).current;
  const valueFieldId =
    props.transactionId && props.transactionId !== -1
      ? `qa-input-value-${props.transactionId}-${props.id}`
      : `qa-input-value-${stableNewTxSuffix}-${props.id}`;

  // Handle delete - call original onDelete and clear AI conversation for this field
  const handleDelete = () => {
    // Clear the AI conversation for this field ID
    dispatch(clearConversationId(valueFieldId));
    // Call the original delete handler
    props.onDelete();
  };

  return (
    <>
      <Row id={props.id} className="mb-3">
        {props.showIsRequiredCheckbox === true && (
          <Col md={1}>
            <Form.Group>
              <Form.Label>{t('editor.required')}</Form.Label>
              <div className={'position-relative'} style={{ top: 4 }}>
                <CheckBox
                  checked={props.matcherValues.isRequired}
                  onCheck={() => {
                    props.onChange('isRequired', !props.matcherValues.isRequired, props.id);
                  }}
                />
              </div>
            </Form.Group>
          </Col>
        )}
        <Col md={3}>
          <Form.Group controlId={'qa-input-name-' + props.id}>
            <Form.Label>{t('editor.matcherName')}</Form.Label>
            <Form.Control
              disabled={props.isReadOnly}
              name="name"
              as="input"
              autoComplete={'off'}
              onChange={(event: any) => handleChange(event, props.id)}
              placeholder={'Enter ' + t('editor.matcherName') + '...'}
              value={props.matcherValues.name ? props.matcherValues.name : ''}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId={'qa-dropdown-op-' + props.id}>
            <Form.Label htmlFor={'qa-toggle-op-' + props.id}>
              {props.shortTitle !== true ? t('editor.matcherOperator') : t('editor.matcherOperatorShort')}
            </Form.Label>
            {props.isWizardModal === true ? (
              <div style={{ marginTop: '6px' }}> Equals </div>
            ) : (
              <MatcherOperatorDropdown
                isReadOnly={props.isReadOnly}
                selected={operatorToSelect}
                operators={dropdownOperators}
                onSelect={(item: DropdownButtonItem) => props.onChange('operator', item.key, props.id)}
                id={'op-' + props.id}
                isShortTitle={props.shortTitle}
              />
            )}
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId={'qa-input-value-' + props.id}>
            <Form.Label>{t('editor.matcherValue')}</Form.Label>
            <div className={'position-relative'}>
              <Form.Control
                id={valueFieldId}
                className={isDataWarningShown ? 'is-warning' : undefined}
                name="value"
                autoComplete={'off'}
                onChange={(event: any) => handleChange(event, props.id)}
                placeholder={isAbsent ? '' : 'Enter ' + t('editor.matcherValue') + '...'}
                value={currentValue}
                disabled={isAbsent || props.isReadOnly}
                style={shouldShowAiIcon ? { paddingRight: '35px' } : undefined}
              />
              {shouldShowAiIcon && (
                <span
                  className="position-absolute"
                  role="button"
                  tabIndex={0}
                  style={{
                    fontSize: '18px',
                    right: '7px',
                    top: '2px',
                    color: '#3272D9',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleAiAssistantShow(valueFieldId)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleAiAssistantShow(valueFieldId);
                    }
                  }}
                >
                  {createAiAssistantIcon()}
                </span>
              )}
            </div>
            {isDataWarningShown && <DataDrivenWarning />}
          </Form.Group>
        </Col>
        <Col>
          <div className={styles.actionButtons}>
            <Button
              disabled={props.isReadOnly}
              variant="danger"
              id={'qa-btn-delete-matcher-' + props.id}
              onClick={handleDelete}
              className="btn-circular"
              title={t('editor.deleteMatcher', { name: props.matcherTitle })}
            >
              <BzmIcon variant={'icon-bzm-delete'} />
            </Button>
            {props.displayAdd && (
              <Button
                disabled={props.isReadOnly}
                variant="secondary"
                id={`qa-btn-add-matcher-${props.matcherTitle.toLowerCase().replace(' ', '-')}`}
                onClick={() => props.onAdd(defaultOperator.key)}
                className="btn-circular ml-4"
                title={t('editor.addNewMatcher', { name: props.matcherTitle })}
              >
                <BzmIcon variant={'icon-bzm-add'} className={'icon-theme-blue'} />
              </Button>
            )}
          </div>
        </Col>
      </Row>
      {showAiAssistant && (
        <AiAssistantModal
          callerId={aiAssistantCallerId}
          updateFunction={handleAiAssistantUpdate}
          currentValue={currentValue}
          onClose={() => setShowAiAssistant(false)}
          title={props.aiAssistantTitle}
          fieldName={props.matcherValues.name}
        />
      )}
    </>
  );
};

export default RequestMatcher;
